import React from 'react';
import Box from '@mui/material/Box';
import UniversalMediaUploaderStatus from '../../../components/universal-media-uploader-status';
import type { HydratedMediaItem } from '../media-type-config';
import type { MultiFileEntry } from '../../../components/machines/multi-file-uploader-types';
import TrackUploadDropzone from './track-upload-dropzone';
import { itemContainerSx, itemDividerSx, trackSeparatorSx } from './styles';

// ---------------------------------------------------------------------------
// UploadStatusEntry — renders a single upload status row with optional track
// ---------------------------------------------------------------------------

interface UploadStatusEntryProps {
  entry: {
    key: string;
    id: string;
    file?: MultiFileEntry['file'];
    progress?: number;
    error?: Error;
    isCompleted?: true;
    wasRecentlyUploaded?: boolean;
    thumbnailUrl?: string;
    attachmentId?: string;
  };
  committedItem: HydratedMediaItem | undefined;
  renderItem: (item: HydratedMediaItem) => React.ReactNode;
  handleClick: (() => void) | undefined;
  isCompletedWithTrack: boolean;
  trackEnabled: boolean;
  uploadTrack?: (mediaID: string, file: File, progress: (...args: unknown[]) => void) => Promise<unknown>;
  onDismiss: () => void;
}

const UploadStatusEntry: React.FC<UploadStatusEntryProps> = ({
  entry,
  committedItem,
  renderItem,
  handleClick,
  isCompletedWithTrack,
  trackEnabled,
  uploadTrack,
  onDismiss,
}) => {
  const { attachmentId } = entry;

  if (committedItem) {
    const showTrack = !!(trackEnabled && uploadTrack && attachmentId);
    return (
      <Box
        sx={{
          display: 'block',
          ...itemDividerSx,
          ...(showTrack ? trackSeparatorSx : {}),
        }}
      >
        <Box sx={itemContainerSx}>
          {renderItem(committedItem)}
        </Box>

        {showTrack && (
          <TrackUploadDropzone
            mediaID={attachmentId}
            uploadTrack={uploadTrack}
          />
        )}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'block',
        ...itemDividerSx,
        ...(isCompletedWithTrack ? trackSeparatorSx : {}),
      }}
    >
      <UniversalMediaUploaderStatus
        avatarSize="large"
        file={entry.file}
        progress={entry.progress}
        error={entry.error}
        isCompleted={entry.isCompleted}
        wasRecentlyUploaded={entry.wasRecentlyUploaded}
        thumbnailURL={entry.thumbnailUrl}
        onClose={onDismiss}
        onClick={handleClick}
      />

      {isCompletedWithTrack && (
        <TrackUploadDropzone
          mediaID={attachmentId!}
          uploadTrack={uploadTrack!}
        />
      )}
    </Box>
  );
};

export type { UploadStatusEntryProps };
export default UploadStatusEntry;
